type FeatureFlagEnv = {
  [key in keyof ImportMetaEnv as key extends `ELEVATE_FF_${string}` ? key : never]: ImportMetaEnv[key];
};

type FeatureFlagName = keyof FeatureFlagEnv extends `ELEVATE_FF_${infer T}` ? T : never;

type FFValue<K extends FeatureFlagName> = FeatureFlagEnv[`ELEVATE_FF_${K}`] extends 'true' | 'false'
  ? boolean
  : FeatureFlagEnv[`ELEVATE_FF_${K}`];

type IFeatureFlag<FF extends FeatureFlagName> = {
  /** Default value absent the environment variable or local development override */
  valueDefault: FFValue<FF>;
  /** Default value used in local development - typically doesn't match `valueDefault` */
  valueLocal: FFValue<FF>;
};

class FeatureFlag<FF extends FeatureFlagName> implements IFeatureFlag<FF> {
  readonly envKey: `ELEVATE_FF_${FF}`;
  constructor(
    readonly name: FF,
    readonly valueDefault: FFValue<FF>,
    readonly valueLocal: FFValue<FF>
  ) {
    this.envKey = `ELEVATE_FF_${name}` as const;
  }

  get value(): FFValue<FF> {
    if (import.meta.env.MODE === 'development') return this.valueLocal;
    const envValue = import.meta.env[`ELEVATE_FF_${this.name}`];
    if (!envValue) return this.valueDefault;
    return (envValue === 'true' || envValue === 'false' ? envValue === 'true' : envValue) as FFValue<FF>;
  }
}

export class DynamicFeatureFlag<FF extends FeatureFlagName> extends FeatureFlag<FF> {
  private _preferredValue: FFValue<FF> | undefined;
  constructor(staticFlag: FeatureFlag<FF>, preferredValue?: FFValue<FF>) {
    super(staticFlag.name, staticFlag.valueDefault, staticFlag.valueLocal);
    this._preferredValue = preferredValue;
  }

  get preferredValue() {
    return this._preferredValue;
  }

  set preferredValue(value: FFValue<FF> | undefined) {
    this._preferredValue = value;
  }

  get value(): FFValue<FF> {
    return this._preferredValue ?? super.value;
  }
}

/**
 * Controls enablement of the Candid Chat UI components (preferences, etc.)
 *
 * - Environment variable: `ELEVATE_FF_PROMOHUB_ENABLED`
 * - Environment default: `false` (disabled)
 * - Local development default: `true` (enabled)
 */
const PROMOHUB_ENABLED = new FeatureFlag('PROMOHUB_ENABLED', false, true);

/**
 * Controls enablement of the Question Bank UI components (preferences, etc.)
 *
 * - Environment variable: `ELEVATE_FF_QUESTION_BANK_ENABLED`
 * - Environment default: `false` (disabled)
 * - Local development default: `true` (enabled)
 */
const QUESTION_BANK_ENABLED = new FeatureFlag('QUESTION_BANK_ENABLED', false, true);

/**
 * Whether this environment is using the `classic` `OrgRoleLDAPGroups` flat model
 * or the new `platform` `RolePermissionGroup` model.
 *
 * - Environment variable: `ELEVATE_FF_PERMISSION_GROUP_MODEL`
 * - Environment default: `classic`
 * - Local development default: `classic`
 */
const PERMISSION_GROUP_MODEL = new FeatureFlag('PERMISSION_GROUP_MODEL', 'classic', 'classic');

/**
 * Whether CloudWatch RUM agent should be enabled.
 **
 * - Environment variable: `ELEVATE_FF_RUM_ENABLED`
 * - Environment default: `false` (disabled)
 * - Local development default: `false` (disabled)
 */
const RUM_ENABLED = new FeatureFlag('RUM_ENABLED', false, false);

/**
 * Whether x-ray tracing should be enabled in CloudWatch RUM. This has no effect if RUM is disabled.
 *
 * - Environment variable: `ELEVATE_FF_XRAY_ENABLED`
 * - Environment default: `false` (disabled)
 * - Local development default: `false` (disabled)
 */
const XRAY_ENABLED = new FeatureFlag('XRAY_ENABLED', false, false);

export default {
  PROMOHUB_ENABLED,
  QUESTION_BANK_ENABLED,
  PERMISSION_GROUP_MODEL,
  RUM_ENABLED,
  XRAY_ENABLED,
};
